// Phase SIKA-5b/2 — Panel 02: Weekly Intelligence.
//
// Source: video frames 001/010/015. Layout (all INSIDE this single panel):
//   - Header: PANEL 02 eyebrow + UPCOMING WINDOW pill
//   - Sub: Week N · Weekend window · target× target
//   - Yellow WHY callout (GAP / BANKED / WEEKS / ADJ row)
//   - L1 hairline row: Pill + match + FOUNDATION badge + confidence stat
//     + per-leg SIKA → reasoning
//   - L2 hairline row: same shape, AMPLIFIER badge
//   - Footer: COMBINED odds + KELLY STAKE FROM ROLLOVER + LOCK THIS WEEK CTA
//
// SlipDetailCard.tsx is deleted in this commit; its content now lives here.
//
// Doctrine:
//   N07 — Per-leg SIKA → reasoning is mandatory.
//   Promotion-not-restriction copy throughout.

import { Badge, Eyebrow, HairlineRow, Pill, Stat } from "../super-acca";
import type {
  SikaCycleLegRow,
  SikaCycleRow,
  SikaGoalRow,
} from "../../sika/useSikaGoal.ts";
import { prefixSikaReasoning } from "../../sika/messageTemplates.ts";

export interface WeeklyIntelPanelProps {
  goal: SikaGoalRow | null;
  cycles: SikaCycleRow[];
  /** Legs for the latest published cycle. Page is responsible for slicing. */
  legs?: SikaCycleLegRow[];
  pro?: boolean;
  onLock?: () => void;
}

function ghc(amount: number): string {
  return `GH₵${amount.toLocaleString("en-GH", { maximumFractionDigits: 0 })}`;
}

function pickLatestPublished(cycles: SikaCycleRow[]): SikaCycleRow | null {
  const visible = cycles.filter((c) =>
    ["published", "locked", "settled"].includes(c.status),
  );
  if (visible.length === 0) return null;
  return visible.reduce<SikaCycleRow>(
    (latest, cur) => (cur.cycle_number > latest.cycle_number ? cur : latest),
    visible[0]!,
  );
}

function weeksRemaining(deadline: string | null): number {
  if (!deadline) return 0;
  const ms = new Date(deadline).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.ceil(ms / (7 * 24 * 60 * 60 * 1000));
}

function statusBadge(c: SikaCycleRow): {
  variant: "amber" | "success" | "danger" | "info" | "ghost";
  label: string;
} {
  if (c.status === "settled") {
    if (c.outcome === "won") return { variant: "success", label: "WON" };
    if (c.outcome === "lost") return { variant: "danger", label: "LOST" };
    if (c.outcome === "void") return { variant: "info", label: "VOID" };
    return { variant: "info", label: "SETTLED" };
  }
  if (c.status === "locked") return { variant: "amber", label: "LOCKED" };
  if (c.status === "published") return { variant: "success", label: "PUBLISHED" };
  return { variant: "ghost", label: c.status.toUpperCase() };
}

function tierBadge(idx: number): { variant: "amber" | "success" | "info"; label: string } {
  if (idx === 0) return { variant: "amber", label: "FOUNDATION" };
  return { variant: "info", label: "AMPLIFIER" };
}

function teaserReasoning(text: string, wordCount = 8): string {
  const t = prefixSikaReasoning(text);
  const words = t.trim().split(/\s+/);
  if (words.length <= wordCount + 2) return t;
  return `${words.slice(0, wordCount + 2).join(" ")}… 🔒 Unlock with Pro`;
}

export function WeeklyIntelPanel({
  goal,
  cycles,
  legs = [],
  pro = false,
  onLock,
}: WeeklyIntelPanelProps) {
  const cycle = pickLatestPublished(cycles);

  if (!cycle) {
    return (
      <div style={{ padding: 22 }} data-panel="weekly-intel">
        <Eyebrow>— Panel 02</Eyebrow>
        <div
          style={{
            marginTop: 6,
            fontFamily: "var(--sa-font-stat)",
            fontSize: 22,
            letterSpacing: "0.02em",
            color: "var(--sa-fg-primary)",
          }}
        >
          Weekly Intelligence
        </div>
        <div
          style={{
            marginTop: 14,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12.5,
            color: "var(--sa-fg-muted)",
            lineHeight: 1.55,
          }}
          data-testid="sika-weekly-intel-empty"
        >
          SIKA is preparing this week's intelligence. The Goal Agent runs Mon 06:00 WAT
          (midweek) and Thu 06:00 WAT (weekend). The next published cycle will appear
          here.
        </div>
      </div>
    );
  }

  const badge = statusBadge(cycle);
  const windowLabel = cycle.window_label === "weekend" ? "Weekend window" : "Midweek window";
  const target = cycle.dynamic_target;
  const wks = goal ? weeksRemaining(goal.deadline) : 0;
  const remaining = goal ? Math.max(0, goal.target_amount - goal.current_amount) : 0;
  const banked = goal ? Number(goal.banked_amount ?? 0) : 0;
  const bankedPct = goal && goal.target_amount > 0
    ? Math.round((banked / goal.target_amount) * 100)
    : 0;
  const rationale = cycle.dynamic_target_rationale ?? null;
  const adj = rationale && typeof rationale.timeline_multiplier === "number"
    ? Number(rationale.timeline_multiplier) - 1
    : 0;
  const adjStr = adj === 0
    ? ""
    : ` · ADJ ${adj > 0 ? "+" : ""}${adj.toFixed(2)} (${adj > 0 ? "behind" : "ahead"})`;

  const combined = legs.length > 0 ? legs.reduce((acc, l) => acc * Number(l.odds), 1) : 0;
  const kelly = cycle.stake_amount ?? 0;
  const overshoot = combined > target;

  return (
    <div style={{ padding: 22 }} data-panel="weekly-intel">
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <Eyebrow>— Panel 02</Eyebrow>
          <div
            style={{
              marginTop: 6,
              fontFamily: "var(--sa-font-stat)",
              fontSize: 22,
              letterSpacing: "0.02em",
              color: "var(--sa-fg-primary)",
            }}
          >
            Weekly Intelligence
          </div>
          <div
            style={{
              marginTop: 4,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              color: "var(--sa-fg-muted)",
              letterSpacing: "0.06em",
            }}
          >
            Week {String(cycle.cycle_number)} · {windowLabel} · {target.toFixed(2)}× target
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Pill
            borderColor="var(--sa-accent-saffron)"
            color="var(--sa-accent-saffron)"
          >
            UPCOMING · WINDOW OPENS SAT 09:00
          </Pill>
          <Badge variant={badge.variant}>{badge.label}</Badge>
          {cycle.is_urgent ? <Badge variant="warn">URGENT</Badge> : null}
          {cycle.recalibrated ? <Badge variant="amber">RECALIBRATED</Badge> : null}
        </div>
      </div>

      {/* Yellow WHY callout */}
      <div
        data-testid="sika-why-callout"
        style={{
          marginTop: 16,
          padding: "14px 14px",
          border: "1px solid var(--sa-accent-saffron)",
          borderRadius: 4,
          background: "rgba(255,200,64,0.08)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "var(--sa-accent-saffron)",
          }}
        >
          Why {target.toFixed(1)}× this week
        </div>
        <div
          style={{
            marginTop: 8,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.5,
          }}
        >
          {goal
            ? `${ghc(remaining)} remaining across ${String(wks)} weeks. ${target.toFixed(1)}× target balances growth and protection at this stage.`
            : `${target.toFixed(1)}× target balances growth and protection at this stage.`}
        </div>
        <div
          style={{
            marginTop: 10,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10.5,
            letterSpacing: "0.10em",
            color: "var(--sa-fg-muted)",
          }}
        >
          GAP {ghc(remaining)} · BANKED {String(bankedPct)}% · WEEKS {String(wks)}{adjStr}
        </div>
      </div>

      {/* Slip rows — inside Panel 02 (SlipDetailCard folded in here) */}
      {legs.length === 0 ? (
        <div
          style={{
            marginTop: 16,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 12,
            color: "var(--sa-fg-muted)",
            lineHeight: 1.5,
          }}
          data-testid="sika-weekly-intel-no-legs"
        >
          No slip published for this cycle yet. SIKA's selection lands here once the
          quality gate clears.
        </div>
      ) : (
        <div style={{ marginTop: 8 }} data-testid="sika-slip-rows">
          {legs.map((leg, idx) => {
            const tb = tierBadge(idx);
            const reasoning = pro
              ? prefixSikaReasoning(leg.goal_reasoning)
              : teaserReasoning(leg.goal_reasoning);
            return (
              <HairlineRow key={leg.leg_id} first={idx === 0}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <Pill>L{String(idx + 1)}</Pill>
                    <span
                      style={{
                        fontFamily: "var(--sa-font-stat)",
                        fontSize: 12,
                        color: "var(--sa-fg-primary)",
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                      }}
                    >
                      {leg.match_name}
                    </span>
                    <Badge variant={tb.variant}>{tb.label}</Badge>
                    {leg.confidence_score !== null ? (
                      <span style={{ marginLeft: "auto" }}>
                        <Pill
                          borderColor="var(--sa-signal-green)"
                          color="var(--sa-signal-green)"
                        >
                          {`STRONG ${String(Math.round(leg.confidence_score))}/100`}
                        </Pill>
                      </span>
                    ) : null}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--sa-font-mono)",
                      fontSize: 11,
                      color: "var(--sa-fg-muted)",
                      marginTop: 4,
                    }}
                  >
                    {leg.market ?? "—"} — {leg.pick ?? "—"} @ {leg.odds.toFixed(2)}
                  </div>
                  <div
                    data-testid="slip-leg-reasoning"
                    style={{
                      fontFamily: "var(--sa-font-mono)",
                      fontSize: 11.5,
                      color: "var(--sa-fg-secondary)",
                      marginTop: 6,
                      lineHeight: 1.5,
                    }}
                  >
                    {reasoning}
                  </div>
                </div>
              </HairlineRow>
            );
          })}
        </div>
      )}

      {/* Footer — COMBINED + KELLY + LOCK CTA. Only renders when legs are present. */}
      {legs.length > 0 ? (
        <div
          style={{
            marginTop: 18,
            paddingTop: 16,
            borderTop: "1px solid var(--sa-hairline)",
            display: "grid",
            gridTemplateColumns: "1fr 1fr auto",
            gap: 16,
            alignItems: "center",
            flexWrap: "wrap",
          }}
          data-testid="sika-slip-footer"
        >
          <div>
            <Stat
              label={`Combined ${combined.toFixed(2)}×`}
              value={`${combined.toFixed(2)}×`}
              accent
            />
            <div
              style={{
                marginTop: 6,
                fontFamily: "var(--sa-font-mono)",
                fontSize: 10.5,
                color: "var(--sa-fg-muted)",
                lineHeight: 1.4,
              }}
            >
              {overshoot
                ? `Within your ${target.toFixed(1)}× target range — small overshoot acceptable.`
                : `On target — within your ${target.toFixed(1)}× plan for the week.`}
            </div>
          </div>
          <div>
            <Stat
              label="Kelly stake — from ROLLOVER"
              value={ghc(kelly)}
              accent
            />
          </div>
          <button
            type="button"
            onClick={onLock}
            data-testid="sika-lock-this-week"
            style={{
              padding: "12px 20px",
              background: "var(--sa-accent-saffron)",
              color: "var(--sa-bg-primary)",
              border: "none",
              borderRadius: 3,
              fontFamily: "var(--sa-font-stat)",
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              cursor: onLock ? "pointer" : "default",
              opacity: onLock ? 1 : 0.85,
            }}
          >
            LOCK THIS WEEK
          </button>
        </div>
      ) : null}

      {pro && rationale ? (
        <div
          style={{
            marginTop: 14,
            padding: 12,
            border: "1px solid var(--sa-hairline)",
            borderRadius: 3,
          }}
        >
          <div
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 10,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "var(--sa-accent-saffron)",
            }}
          >
            Dynamic target rationale
          </div>
          <div
            style={{
              marginTop: 8,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              color: "var(--sa-fg-secondary)",
              lineHeight: 1.5,
            }}
          >
            Gap drove a base {String(rationale.base_target ?? "—")}× target.
            Risk budget × {String(rationale.risk_budget_multiplier ?? "—")}.
            Timeline × {String(rationale.timeline_multiplier ?? "—")}.
            {rationale.is_urgent === true
              ? " Urgent mode active — increased risk acknowledged."
              : ""}
          </div>
        </div>
      ) : null}
    </div>
  );
}
